import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  ErrorState,
  ForbiddenState,
  Icon,
  StateMessage,
} from "@/ds";
import { TipoObraTag } from "@/shared/components";
import { DominanciaBloco } from "../dominancia-bloco";
import { CampoDestinoCancelamento } from "../campo-destino-cancelamento";
import { JustificativaField } from "@/shared/components";
import { ServicosMateriaisSecao } from "../servicos-materiais-secao";
import { FecharTooltip, fecharPt } from "../fechar-tooltip";
import {
  CANCELAR_LABEL,
  CANCELAR_TIPO_TITLE,
  CARREGANDO_LABEL,
  COLUNA_CLASSIFICACAO,
  COLUNA_DISTRIBUICAO,
  COLUNA_ESCOPO,
  COLUNA_TOTAL,
  ESCOPOS_TITLE,
  LINHA_TOTAL,
  TIPO_A_CANCELAR_LABEL,
  TIPO_EM_CANCELAMENTO_LABEL,
  VALOR_TOTAL_LABEL,
} from "./cancelar-tipo-obra-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./cancelar-tipo-obra-dialog.module.scss";
import type { CancelarTipoObraDialogViewModel } from "./types-cancelar-tipo-obra-dialog";

const FECHAR_TESTID = "cancelar-tipo-fechar-x";
const DIALOGO_PT = { footer: { className: modal.dialogoRodape }, ...fecharPt(FECHAR_TESTID) };

type CartaoTipoProps = Pick<CancelarTipoObraDialogViewModel, "tipo"> & { rotulo: string };

function CartaoTipo({ tipo, rotulo }: CartaoTipoProps) {
  return (
    <section className={styles.cartao} data-testid="cancelar-tipo-cartao">
      <div className={styles.cartaoTopo}>
        <span className={styles.rotulo}>{rotulo}</span>
        {tipo.valorTotal ? (
          <span className={styles.valorTotal} data-testid="cancelar-tipo-valor">
            {VALOR_TOTAL_LABEL}: {tipo.valorTotal}
          </span>
        ) : null}
      </div>
      <div className={styles.identificacao}>
        <TipoObraTag tipo={tipo.codigo} />
        <div className={styles.textos}>
          <strong className={styles.nome} data-testid="cancelar-tipo-nome">
            {tipo.titulo}
          </strong>
          <span className={styles.detalhe} data-testid="cancelar-tipo-detalhe">
            {tipo.detalhe}
          </span>
        </div>
        <Tag value={tipo.classificacao} severity="info" />
      </div>
    </section>
  );
}

export function CancelarTipoObraDialogView({
  formId,
  estado,
  errorMessage,
  recarregar,
  tipo,
  escopos,
  totalEscopos,
  servicosMateriais,
  sucessao,
  confirmarLabel,
  destino,
  avisoImpacto,
  orientacaoUltimoTipo,
  justificativa,
  justificativaError,
  justificativaObrigatoria,
  isSubmitting,
  submitError,
  confirmarDesabilitado,
  escolherDestino,
  digitarJustificativa,
  enviar,
  cancelar,
  descarte,
}: CancelarTipoObraDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="xl"
        className={estado === "ultimo-tipo" ? styles.dialogoUltimo : modal.dialogoFicha}
        contentClassName={estado === "ultimo-tipo" ? styles.corpoUltimo : modal.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={CANCELAR_TIPO_TITLE} />}
        pt={DIALOGO_PT}
        footer={
          estado === "ultimo-tipo" ? (
            // Exceção à ADR 0008 (decisão do usuário 30/10): DialogFooter do DS não tem rodapé de uma ação.
            <div className={modal.rodape}>
              <Button
                type="button"
                severity="secondary"
                label={CANCELAR_LABEL}
                onClick={cancelar}
                data-testid="cancelar-tipo-fechar"
              />
            </div>
          ) : (
            <DialogFooter
              className={modal.rodape}
              cancelLabel={CANCELAR_LABEL}
              confirmLabel={confirmarLabel}
              severity="danger"
              confirmButtonProps={{
                disabled: confirmarDesabilitado,
              }}
              formId={formId}
              loading={isSubmitting}
              locked={isSubmitting}
              onCancel={cancelar}
              testId="cancelar-tipo"
            />
          )
        }
        data-testid="cancelar-tipo-obra-dialog"
      >
        <FecharTooltip testId={FECHAR_TESTID} />
        {estado === "carregando" ? (
          <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="cancelar-tipo-loading" />
        ) : null}
        {estado === "proibido" ? (
          <ForbiddenState description={errorMessage ?? undefined} testId="cancelar-tipo-error" />
        ) : null}
        {estado === "erro" ? (
          <ErrorState
            message={errorMessage ?? undefined}
            onRetry={recarregar}
            testId="cancelar-tipo-error"
          />
        ) : null}

        {estado === "ultimo-tipo" ? (
          <div className={styles.ultimo}>
            <CartaoTipo tipo={tipo} rotulo={TIPO_EM_CANCELAMENTO_LABEL} />
            <Message
              className="p-message-block"
              severity="warn"
              icon={<Icon name="exclamation-triangle" size={18} />}
              text={orientacaoUltimoTipo ?? undefined}
              pt={{ text: { className: styles.alertaTexto } }}
              data-testid="cancelar-tipo-ultimo"
            />
          </div>
        ) : null}

        {estado === "pronto" ? (
          <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
            <CartaoTipo tipo={tipo} rotulo={TIPO_A_CANCELAR_LABEL} />

            {sucessao ? <DominanciaBloco {...sucessao} /> : null}

            {escopos.length > 0 ? (
              <>
                <h3 className={styles.secao}>{ESCOPOS_TITLE}</h3>
                <div className={styles.tabelaWrap}>
                  <table className={styles.tabela} data-testid="cancelar-tipo-escopos">
                    <thead>
                      <tr>
                        <th scope="col">{COLUNA_ESCOPO}</th>
                        <th scope="col">{COLUNA_CLASSIFICACAO}</th>
                        <th scope="col">{COLUNA_DISTRIBUICAO}</th>
                        <th scope="col">{COLUNA_TOTAL}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {escopos.map((escopo) => (
                        <tr key={escopo.id} data-testid="cancelar-tipo-escopo">
                          <td>{escopo.nome}</td>
                          <td>
                            <Tag value={escopo.classificacao} severity="info" />
                          </td>
                          <td data-testid="cancelar-tipo-escopo-distribuicao">
                            {escopo.distribuicao}
                          </td>
                          <td>{escopo.total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>{LINHA_TOTAL}</td>
                        <td data-testid="cancelar-tipo-total">{totalEscopos}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : null}

            <ServicosMateriaisSecao {...servicosMateriais} />

            {destino ? (
              <CampoDestinoCancelamento
                value={destino.value}
                onChange={escolherDestino}
                opcoes={destino.opcoes}
                error={destino.error}
                disabled={isSubmitting}
                testId="cancelar-tipo-destino"
                errorTestId="cancelar-tipo-destino-error"
              />
            ) : null}
            {escopos.length > 0 ? (
              <Message
                className="p-message-block"
                severity="warn"
                icon={<Icon name="exclamation-triangle" size={18} />}
                text={avisoImpacto}
                pt={{ text: { className: styles.alertaTexto } }}
                data-testid="cancelar-tipo-impacto"
              />
            ) : null}

            <JustificativaField
              labelClassName={modal.rotuloCampo}
              rows={2}
              value={justificativa}
              onChange={digitarJustificativa}
              error={justificativaError}
              semAsterisco={!justificativaObrigatoria}
              disabled={isSubmitting}
              testIds={{
                campo: "cancelar-tipo-justificativa",
                contador: "cancelar-tipo-contador",
                erro: "cancelar-tipo-justificativa-error",
              }}
            />

            {submitError ? (
              <Message
                className="p-message-block"
                severity="error"
                text={submitError}
                data-testid="cancelar-tipo-submit-error"
              />
            ) : null}
          </form>
        ) : null}
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
